/* src/pages/TextMessaging.jsx — Option B B2b: self-serve SMS onboarding wizard */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { collection, doc, getDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useToast } from '../contexts/ToastContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { isAdmin } from '../utils/admin';
import { SMS_ONBOARDING_LAUNCHED } from '../utils/flags';
import { smsApi, walletApi } from '../utils/api';
import {
  MISSED_CALL_CHECKBOX_ONELINER,
  PAST_DUE_STATUS_LABEL,
  SOLE_PROP_MOBILE_OTP_COPY,
  buildSmsPendingStepper,
  maskUsPhoneForToast,
  smsNextSteps,
  statusLabel,
} from '../utils/smsOnboardingUx';

const STEPS = ['Business profile', 'Pick number', 'Review campaign', 'Confirm'];

const EMPTY_PROFILE = {
  legalBusinessName: '',
  ein: '',
  isSoleProprietor: false,
  businessType: 'Limited Liability Corporation',
  websiteUrl: '',
  street: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
  contactEmail: '',
  contactPhone: '',
};

const BUSINESS_TYPE_OPTIONS = [
  { value: 'Limited Liability Corporation', label: 'LLC' },
  { value: 'Corporation', label: 'Corporation' },
  { value: 'Partnership', label: 'Partnership' },
  { value: 'Sole Proprietorship', label: 'Sole Proprietorship' },
  { value: 'Non-profit Corporation', label: 'Non-profit' },
];

const PHONE_KEYPAD = {
  A: '2', B: '2', C: '2', D: '3', E: '3', F: '3', G: '4', H: '4', I: '4',
  J: '5', K: '5', L: '5', M: '6', N: '6', O: '6', P: '7', Q: '7', R: '7', S: '7',
  T: '8', U: '8', V: '8', W: '9', X: '9', Y: '9', Z: '9',
};

function normalizePhoneContains(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';
  return trimmed
    .toUpperCase()
    .split('')
    .map((ch) => {
      if (ch >= '0' && ch <= '9') return ch;
      if (ch === '*') return ch;
      return PHONE_KEYPAD[ch] || '';
    })
    .join('');
}

function normalizeWebsiteUrl(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/+/, '')}`;
}

export { PAST_DUE_STATUS_LABEL, SOLE_PROP_MOBILE_OTP_COPY, statusLabel };

const QUIET_HOURS_ACCEPTANCE_COPY =
  "I understand that TCPA and carrier best practices recommend restricting SMS to 8am–9pm in the recipient's local time zone. I accept full responsibility for any messages sent outside these hours if I disable quiet hours enforcement on my workspace.";

export const SMS_PAGE_INTRO_COPY =
  'This is where you get a dedicated phone number so your forms can text leads — $49 setup + $19.99/mo, plus per-text from your wallet.';

const SMS_TRIAL_DISCLOSURE_COPY =
  '14-day free trial covers chat, email and AI replies. Text messaging is a paid add-on — a number and wallet credit are required.';

const SMS_ADDON_PRICING_COPY =
  '$49 one-time setup + $19.99/mo platform fee + per-text billed from wallet. Includes dedicated number + A2P carrier registration (typically 1–3 business days).';

const SMS_NUMBER_PICKER_PRICING_COPY =
  '$49 one-time setup + $19.99/mo platform fee (wallet). Outbound texts billed at $0.015/segment.';

function normalizeUsMobile(raw) {
  const trimmed = String(raw || '').trim();
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (trimmed.startsWith('+') && digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

function isValidUsMobile(raw) {
  const normalized = normalizeUsMobile(raw);
  return normalized != null && /^\+1[2-9]\d{9}$/.test(normalized);
}

function isValidEin(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return false;
  return /^\d{2}-?\d{7}$/.test(trimmed);
}

function validateBusinessProfileStep(profile) {
  const solePropChecked = profile.isSoleProprietor === true;
  const missing = [];
  if (!profile.legalBusinessName?.trim()) missing.push('legal business name');
  if (!profile.street?.trim()) missing.push('street address');
  if (!profile.city?.trim()) missing.push('city');
  if (!profile.state?.trim()) missing.push('state');
  if (!profile.postalCode?.trim()) missing.push('ZIP code');
  if (!profile.contactEmail?.trim()) missing.push('contact email');

  if (solePropChecked && profile.ein?.trim()) {
    return {
      ok: false,
      error: 'Sole proprietor registration cannot include an EIN — uncheck sole proprietor or remove the EIN.',
    };
  }
  if (!solePropChecked && !profile.ein?.trim()) {
    return { ok: false, error: 'EIN is required unless registering as a sole proprietor.' };
  }
  if (!solePropChecked && profile.ein?.trim() && !isValidEin(profile.ein)) {
    return { ok: false, error: 'EIN must be 9 digits (XX-XXXXXXX).' };
  }

  if (!profile.contactPhone?.trim()) {
    missing.push(solePropChecked ? 'mobile phone' : 'business contact phone');
  } else if (!isValidUsMobile(profile.contactPhone)) {
    return {
      ok: false,
      error: 'Enter a valid US phone number (10 digits, or +1 followed by 10 digits).',
    };
  }

  if (!solePropChecked && !normalizeWebsiteUrl(profile.websiteUrl)) {
    missing.push('business website');
  }

  if (missing.length) {
    return { ok: false, error: `Required: ${missing.join(', ')}.` };
  }
  return { ok: true };
}

function formatPreviewError(res) {
  const msg = res.error || 'Preview failed';
  return res.code ? `${msg} (${res.code})` : msg;
}

export default function TextMessaging() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile } = useAuth();
  const { selectedWorkspaceId, isOwner } = useWorkspace();
  const { showToast } = useToast();
  const isMobile = useIsMobile();

  const userIsAdmin = isAdmin(currentUser, userProfile);
  const canAccessPage = (SMS_ONBOARDING_LAUNCHED || userIsAdmin) && (isOwner || userIsAdmin);

  const [step, setStep] = useState(0);
  const [wizardStarted, setWizardStarted] = useState(false);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [areaCode, setAreaCode] = useState('');
  const [numberContains, setNumberContains] = useState('');
  const [numbers, setNumbers] = useState([]);
  const [numberSearchAttempted, setNumberSearchAttempted] = useState(false);
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState('');
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [walletUsd, setWalletUsd] = useState(null);
  const [smsNumber, setSmsNumber] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [programTermsAccepted, setProgramTermsAccepted] = useState(false);
  const [quietHoursAcceptance, setQuietHoursAcceptance] = useState(false);
  const [quietHoursSaving, setQuietHoursSaving] = useState(false);
  const [voiceForwardTo, setVoiceForwardTo] = useState('');
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [voiceWhisperEnabled, setVoiceWhisperEnabled] = useState(false);
  const [voiceRingTimeoutSec, setVoiceRingTimeoutSec] = useState(20);
  const [missedCallTextBackEnabled, setMissedCallTextBackEnabled] = useState(false);
  const [voicemailEnabled, setVoicemailEnabled] = useState(false);
  const [voiceSaving, setVoiceSaving] = useState(false);
  const [upgradeRequesting, setUpgradeRequesting] = useState(false);
  const [autoRechargeEnabled, setAutoRechargeEnabled] = useState(false);
  const [testSmsSending, setTestSmsSending] = useState(false);

  const onboardingComplete = smsNumber?.status === 'active' && smsNumber?.a2p?.status === 'approved';
  const onboardingFailed = smsNumber?.a2p?.status === 'failed';
  const isPastDue = smsNumber?.status === 'past_due';
  const onboardingInFlight = smsNumber && !onboardingComplete && smsNumber.status !== 'released';
  const pendingStepper = onboardingInFlight && !isPastDue ? buildSmsPendingStepper(smsNumber) : null;
  const nextSteps = onboardingComplete
    ? smsNextSteps({ forms, smsNumber, autoRechargeEnabled })
    : [];

  const loadStatus = useCallback(async () => {
    if (!selectedWorkspaceId) return;
    try {
      const res = await smsApi.getSmsStatus(selectedWorkspaceId);
      setSmsNumber(res.smsNumber || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingStatus(false);
    }
  }, [selectedWorkspaceId]);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!canAccessPage) return;
    loadStatus();
  }, [currentUser, canAccessPage, loadStatus, navigate]);

  useEffect(() => {
    if (!onboardingInFlight || !selectedWorkspaceId) return undefined;
    const id = setInterval(loadStatus, 30000);
    return () => clearInterval(id);
  }, [onboardingInFlight, selectedWorkspaceId, loadStatus]);

  useEffect(() => {
    if (!smsNumber) return;
    setVoiceEnabled(smsNumber.voiceEnabled === true);
    setVoiceForwardTo(smsNumber.voiceForwardToE164 || '');
    setVoiceWhisperEnabled(smsNumber.voiceWhisperEnabled === true);
    setVoiceRingTimeoutSec(smsNumber.voiceRingTimeoutSec ?? 20);
    setMissedCallTextBackEnabled(smsNumber.missedCallTextBackEnabled === true);
    setVoicemailEnabled(smsNumber.voicemailEnabled === true);
  }, [
    smsNumber?.voiceEnabled,
    smsNumber?.voiceForwardToE164,
    smsNumber?.voiceWhisperEnabled,
    smsNumber?.voiceRingTimeoutSec,
    smsNumber?.missedCallTextBackEnabled,
    smsNumber?.voicemailEnabled,
  ]);

  useEffect(() => {
    async function prefill() {
      if (!selectedWorkspaceId || !currentUser) return;
      try {
        const wsSnap = await getDoc(doc(db, 'workspaces', selectedWorkspaceId));
        const ws = wsSnap.exists() ? wsSnap.data() : {};
        setProfile((p) => ({
          ...p,
          legalBusinessName: ws.businessName || ws.name || p.legalBusinessName,
          contactEmail: ws.ownerEmail || currentUser.email || p.contactEmail,
        }));

        const formsSnap = await getDocs(
          query(collection(db, 'forms'), where('workspaceId', '==', selectedWorkspaceId))
        );
        const list = formsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setForms(list);
        const fromUrl = new URLSearchParams(location.search).get('formId');
        if (fromUrl && list.some((f) => f.id === fromUrl)) {
          setSelectedFormId(fromUrl);
        } else if (list.length === 1) {
          setSelectedFormId(list[0].id);
        }

        const formWebsite =
          list.find((f) => f.settings?.websiteUrl)?.settings?.websiteUrl ||
          list.find((f) => f.settings?.websiteUrls?.length)?.settings?.websiteUrls?.[0] ||
          ws.websiteUrl ||
          '';
        setProfile((p) => ({
          ...p,
          websiteUrl: p.websiteUrl || formWebsite || '',
        }));

        const wallet = await walletApi.getWallet(selectedWorkspaceId);
        if (wallet?.wallet) {
          setWalletUsd(wallet.wallet.availableUsd);
          setAutoRechargeEnabled(wallet.wallet.autoRecharge?.enabled === true);
        }
      } catch (e) {
        console.error(e);
      }
    }
    if (canAccessPage) prefill();
  }, [selectedWorkspaceId, currentUser, canAccessPage, location.search]);

  async function handleSearchNumbers() {
    if (!selectedWorkspaceId) return;
    setSearchingNumbers(true);
    setError(null);
    try {
      const res = await smsApi.searchNumbers(
        selectedWorkspaceId,
        areaCode.trim() || undefined,
        normalizePhoneContains(numberContains) || undefined
      );
      setNumbers(res.numbers || []);
      setNumberSearchAttempted(true);
      if (!res.numbers?.length) {
        showToast('No numbers available — try a nearby area code', 'error');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSearchingNumbers(false);
    }
  }

  async function handlePreview() {
    if (!selectedFormId) {
      const msg = 'Select a form for opt-in documentation';
      setError(msg);
      showToast(msg, 'error');
      return { ok: false };
    }
    setPreviewLoading(true);
    setError(null);
    try {
      const profileForPreview = profile.contactPhone?.trim()
        ? { ...profile, contactPhone: normalizeUsMobile(profile.contactPhone) || profile.contactPhone }
        : profile;
      const res = await smsApi.previewCampaign(selectedWorkspaceId, selectedFormId, profileForPreview);
      if (!res.ok || !res.success) {
        const msg = formatPreviewError(res);
        setError(msg);
        showToast(msg, 'error');
        if (res.code === 'OPT_IN_MESSAGE_TOO_LONG') {
          setStep(0);
          setWizardStarted(true);
        }
        return { ok: false, error: msg, code: res.code };
      }
      setPreview(res);
      return { ok: true };
    } catch (e) {
      const msg = e.message || 'Preview failed';
      setError(msg);
      showToast(msg, 'error');
      return { ok: false, error: msg };
    } finally {
      setPreviewLoading(false);
    }
  }

  function handleStep0Continue() {
    const check = validateBusinessProfileStep(profile);
    if (!check.ok) {
      setError(check.error);
      showToast(check.error, 'error');
      return;
    }
    const normalizedPhone = normalizeUsMobile(profile.contactPhone);
    const normalizedWebsite = normalizeWebsiteUrl(profile.websiteUrl);
    setProfile((p) => ({
      ...p,
      ...(normalizedPhone ? { contactPhone: normalizedPhone } : {}),
      ...(normalizedWebsite ? { websiteUrl: normalizedWebsite } : {}),
      ein: p.isSoleProprietor ? '' : p.ein?.trim() || '',
    }));
    setError(null);
    setStep(1);
  }

  async function handleStep1Continue() {
    if (!selectedPhone) return;
    if (selectedFormId) {
      const result = await handlePreview();
      if (!result.ok) return;
      setStep(2);
      return;
    }
    setStep(2);
  }

  async function handleStart() {
    if (!programTermsAccepted) {
      setError('You must accept the SMS Program Terms to continue.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await smsApi.startOnboarding({
        workspaceId: selectedWorkspaceId,
        formId: selectedFormId,
        phoneNumber: selectedPhone,
        businessProfile: profile,
        enableSmsOptInOnForm: true,
        programTermsAccepted: true,
      });
      if (!res.ok) {
        if (res.status === 402) {
          const msg = `Insufficient wallet balance. Add at least $${(res.requiredUsd || 0).toFixed(2)} to continue.`;
          setError(msg);
          showToast(msg, 'error');
        } else {
          const msg = res.error || 'Onboarding failed';
          setError(msg);
          showToast(msg, 'error');
        }
        return;
      }
      setSmsNumber(res.smsNumber);
      showToast('SMS onboarding started', 'success');
      setStep(0);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleQuietHoursOverride(disabled) {
    if (disabled && !quietHoursAcceptance) {
      setError('You must accept responsibility before disabling quiet hours.');
      return;
    }
    setQuietHoursSaving(true);
    setError(null);
    try {
      const res = await smsApi.setQuietHoursOverride({
        workspaceId: selectedWorkspaceId,
        disabled,
        responsibilityAccepted: disabled ? true : undefined,
      });
      if (!res.success) {
        setError(res.error || 'Failed to update quiet hours setting');
        return;
      }
      setSmsNumber((prev) => (prev ? { ...prev, quietHoursDisabled: res.quietHoursDisabled } : prev));
      if (!disabled) setQuietHoursAcceptance(false);
      showToast(
        disabled ? 'Quiet hours enforcement disabled' : 'Quiet hours enforcement re-enabled',
        'success'
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setQuietHoursSaving(false);
    }
  }

  async function handleSaveVoiceSettings() {
    if (!selectedWorkspaceId) return;
    const normalizedForward = normalizeUsMobile(voiceForwardTo);
    if (voiceEnabled && !normalizedForward) {
      setError('Enter a valid US phone number to forward calls to.');
      return;
    }
    setVoiceSaving(true);
    setError(null);
    try {
      const res = await smsApi.updateVoiceSettings({
        workspaceId: selectedWorkspaceId,
        voiceEnabled,
        voiceForwardToE164: normalizedForward,
        voiceWhisperEnabled,
        voiceRingTimeoutSec: Number(voiceRingTimeoutSec) || 20,
        missedCallTextBackEnabled:
          smsNumber?.a2p?.campaignCoversMissedCall === true ? missedCallTextBackEnabled : false,
        voicemailEnabled,
      });
      if (!res.success) {
        setError(res.error || 'Failed to save call forwarding settings');
        return;
      }
      if (res.smsNumber) setSmsNumber(res.smsNumber);
      showToast(voiceEnabled ? 'Call forwarding enabled' : 'Call forwarding saved', 'success');
    } catch (e) {
      setError(e.message);
    } finally {
      setVoiceSaving(false);
    }
  }

  async function handleRequestMissedCallUpgrade() {
    if (!selectedWorkspaceId) return;
    setUpgradeRequesting(true);
    setError(null);
    try {
      const res = await smsApi.requestMissedCallUpgrade(selectedWorkspaceId);
      if (!res.success) {
        setError(res.error || 'Failed to request campaign update');
        return;
      }
      if (res.smsNumber) setSmsNumber(res.smsNumber);
      showToast(
        res.alreadyRequested ? 'Request already on file' : 'Campaign update requested',
        'success'
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setUpgradeRequesting(false);
    }
  }

  async function handleSendOwnerTestSms() {
    if (!selectedWorkspaceId) return;
    setTestSmsSending(true);
    setError(null);
    try {
      const res = await smsApi.sendOwnerTestSms(selectedWorkspaceId);
      if (!res.success) {
        setError(res.error || 'Failed to send test text');
        return;
      }
      if (res.smsNumber) setSmsNumber(res.smsNumber);
      showToast(`Sent to ${maskUsPhoneForToast(res.toPhone)}`, 'success');
    } catch (e) {
      setError(e.message);
    } finally {
      setTestSmsSending(false);
    }
  }

  async function handleRetry(action) {
    setSubmitting(true);
    setError(null);
    try {
      await smsApi.retryOnboarding({
        workspaceId: selectedWorkspaceId,
        action,
        businessProfile: profile,
        formId: selectedFormId || smsNumber?.registeredOptInFormId,
      });
      showToast('Retry submitted', 'success');
      await loadStatus();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  const pagePad = isMobile ? '1rem 1rem 2rem' : '24px 32px 48px';

  const pageSubtitle = onboardingComplete
    ? 'Two-way AI SMS is live — AI replies, manual sends, and STOP/START opt-out run from your inbox.'
    : onboardingInFlight
      ? 'Your SMS number is provisioning or under A2P review. Two-way texting activates once brand and campaign are approved.'
      : 'Provision a dedicated SMS number, register for A2P compliance, and enable two-way AI texting from your inbox.';

  function renderWizardSteps() {
    return (
      <nav className="wizard-steps" aria-label="SMS onboarding steps">
        {STEPS.map((label, i) => (
          <span
            key={label}
            className={[
              'wizard-step-pill',
              i === step ? 'is-active' : '',
              i < step ? 'is-done' : '',
            ].filter(Boolean).join(' ')}
          >
            {i + 1}. {label}
          </span>
        ))}
      </nav>
    );
  }

  if (!canAccessPage) {
    return (
      <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
        <div className="today-page settings-page text-messaging-page" style={{ padding: pagePad }}>
          <div className="today-header">
            <div className="today-header-left" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
              <h1 className="today-h1">Text Messaging</h1>
            </div>
          </div>
          <div className="today-panel settings-section-panel">
            <div className="settings-team-body">
              <p style={{ margin: 0, color: 'var(--muted)', lineHeight: 1.5 }}>
                {!SMS_ONBOARDING_LAUNCHED && !userIsAdmin
                  ? 'SMS onboarding is coming soon.'
                  : 'Ask your workspace owner to set up text messaging.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadingStatus) {
    return (
      <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
        <div className="today-page settings-page text-messaging-page" style={{ padding: pagePad }}>
          <div className="today-header">
            <div className="today-header-left" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
              <h1 className="today-h1">Text Messaging</h1>
            </div>
          </div>
          <p className="today-muted">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
      <div className="today-page settings-page text-messaging-page" style={{ padding: pagePad }}>
        <div className="today-header">
          <div className="today-header-left" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
            <h1 className="today-h1">Text Messaging</h1>
            <p style={{ margin: 0, color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.5 }}>
              {!onboardingInFlight && !onboardingComplete ? SMS_PAGE_INTRO_COPY : pageSubtitle}
            </p>
          </div>
        </div>

      {(onboardingInFlight || onboardingComplete || onboardingFailed) && (
        <div className="today-panel settings-section-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">Status</span>
          </div>
          <div className="settings-team-body">
          <p style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600 }}>
            {statusLabel(smsNumber)}
            {smsNumber?.phoneNumber ? ` — ${smsNumber.phoneNumber}` : ''}
          </p>
          {isPastDue && (
            <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
              <Link to="/billing?topup=1" className="today-link-accent">Top up</Link>
              {' '}to resume texting.
            </p>
          )}
          {onboardingInFlight && !isPastDue && !pendingStepper && (
            <p className="today-muted" style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
              Brand and campaign review typically takes a few hours to several business days. This page refreshes automatically.
            </p>
          )}
          {pendingStepper && (
            <ol style={{ margin: '1rem 0 0', padding: 0, listStyle: 'none' }}>
              {pendingStepper.steps.map((stepItem, idx) => {
                const isActive = stepItem.state === 'active' || stepItem.state === 'failed';
                const mark =
                  stepItem.state === 'done' ? '✓' : stepItem.state === 'failed' ? '✗' : stepItem.state === 'active' ? '●' : '○';
                return (
                  <li
                    key={stepItem.id}
                    style={{
                      marginBottom: idx < pendingStepper.steps.length - 1 ? '0.65rem' : 0,
                      fontSize: '0.9rem',
                      color: stepItem.state === 'done' ? 'var(--muted)' : 'inherit',
                    }}
                  >
                    <span style={{ fontWeight: isActive ? 600 : 400 }}>
                      {mark} {stepItem.label}
                    </span>
                    {isActive && stepItem.happening && (
                      <div className="today-muted" style={{ margin: '0.25rem 0 0 1.25rem', fontSize: '0.85rem', lineHeight: 1.45 }}>
                        <div>Happening: {stepItem.happening}</div>
                        {stepItem.you && <div>You: {stepItem.you}</div>}
                        {stepItem.id === 'otp' && stepItem.instruction && (
                          <div style={{ marginTop: '0.35rem' }}>{stepItem.instruction}</div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
          {smsNumber?.a2p?.failureReason && (
            <div className="fb-callout warn" style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
              {smsNumber.a2p.failureReason}
            </div>
          )}
          {onboardingFailed && (
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button type="button" className="inbox-pill-btn-primary" disabled={submitting} onClick={() => handleRetry('brand')}>
                Retry brand
              </button>
              <button type="button" className="inbox-pill-btn" disabled={submitting} onClick={() => handleRetry('campaign')}>
                Retry campaign
              </button>
            </div>
          )}
          {onboardingComplete && (
            <>
              <p style={{ marginTop: '0.75rem', fontSize: '0.9rem' }}>
                Compose from{' '}
                <Link to="/leads" className="today-link-accent">Inbox</Link>.
              </p>
              {isOwner && (
                <div style={{ marginTop: '1rem' }}>
                  <span className="today-panel-title" style={{ fontSize: '0.95rem' }}>
                    Next steps
                  </span>
                  <ul style={{ margin: '0.65rem 0 0', padding: 0, listStyle: 'none' }}>
                    {nextSteps.map((row) => (
                      <li
                        key={row.id}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.6rem',
                          marginBottom: '0.7rem',
                        }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            color: row.done ? '#28a745' : '#bbb',
                            fontWeight: 700,
                            marginTop: '0.1rem',
                          }}
                        >
                          {row.done ? '✓' : '○'}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: '0.9rem',
                              textDecoration: row.done ? 'line-through' : 'none',
                              color: row.done ? 'var(--muted)' : 'inherit',
                            }}
                          >
                            {row.label}
                          </div>
                          <div className="today-muted" style={{ fontSize: '0.8rem', lineHeight: 1.45, marginTop: '0.15rem' }}>
                            {row.sub}
                          </div>
                          {!row.done && row.href && (
                            <Link
                              to={row.href}
                              className="today-link-accent"
                              style={{ display: 'inline-block', marginTop: '0.25rem', fontSize: '0.82rem' }}
                            >
                              {row.id === 'optin' ? 'Open Operator' : row.id === 'forward' ? 'Set forwarding' : row.id === 'autocharge' ? 'Open Billing' : 'Open'}
                            </Link>
                          )}
                          {!row.done && row.action === 'text-me' && smsNumber?.externallyManaged !== true && (
                            <button
                              type="button"
                              className="inbox-pill-btn"
                              style={{ marginTop: '0.35rem' }}
                              disabled={testSmsSending}
                              onClick={handleSendOwnerTestSms}
                            >
                              {testSmsSending ? 'Sending…' : 'Text me'}
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {typeof smsNumber?.segmentsSentToday === 'number' && smsNumber?.dailySegmentCap && (
                <p className="today-muted" style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                  Segments sent today: {smsNumber.segmentsSentToday} / {smsNumber.dailySegmentCap} (UTC day, A2P brand tier cap)
                </p>
              )}
              {isOwner && (
                <div
                  style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    border: '1px solid var(--hairline-2)',
                    borderRadius: 8,
                    background: 'var(--surface-2)',
                  }}
                >
                  <span className="today-panel-title" style={{ fontSize: '0.95rem' }}>
                    Quiet hours (8am–9pm local)
                  </span>
                  <p className="today-muted" style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', lineHeight: 1.5 }}>
                    {smsNumber?.quietHoursDisabled
                      ? 'Quiet hours enforcement is OFF. You are responsible for messages sent outside recommended hours.'
                      : 'Outbound SMS is blocked before 8am and after 9pm in each lead\'s local time zone (with a short reply window after inbound texts).'}
                  </p>
                  {smsNumber?.quietHoursDisabled ? (
                    <button
                      type="button"
                      className="inbox-pill-btn"
                      style={{ marginTop: '0.75rem' }}
                      disabled={quietHoursSaving}
                      onClick={() => handleQuietHoursOverride(false)}
                    >
                      {quietHoursSaving ? 'Saving…' : 'Re-enable quiet hours'}
                    </button>
                  ) : (
                    <>
                      <label className="fb-checkbox-row" style={{ alignItems: 'flex-start', gap: '0.75rem', marginTop: '0.75rem' }}>
                        <input
                          type="checkbox"
                          checked={quietHoursAcceptance}
                          onChange={(e) => setQuietHoursAcceptance(e.target.checked)}
                          style={{ marginTop: '0.2rem' }}
                        />
                        <span style={{ lineHeight: 1.55, fontSize: '0.85rem' }}>{QUIET_HOURS_ACCEPTANCE_COPY}</span>
                      </label>
                      <button
                        type="button"
                        className="inbox-pill-btn"
                        style={{ marginTop: '0.75rem' }}
                        disabled={quietHoursSaving || !quietHoursAcceptance}
                        onClick={() => handleQuietHoursOverride(true)}
                      >
                        {quietHoursSaving ? 'Saving…' : 'Disable quiet hours enforcement'}
                      </button>
                    </>
                  )}
                </div>
              )}
              {isOwner && smsNumber?.externallyManaged !== true && (
                <div
                  id="call-forwarding"
                  style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    border: '1px solid var(--hairline-2)',
                    borderRadius: 8,
                    background: 'var(--surface-2)',
                  }}
                >
                  <span className="today-panel-title" style={{ fontSize: '0.95rem' }}>
                    Call forwarding
                  </span>
                  <p className="today-muted" style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', lineHeight: 1.5 }}>
                    Calls to your gocta number ring your phone. Caller ID shows the caller&apos;s number (or your gocta number if blocked).
                  </p>
                  <label className="fb-label" style={{ marginTop: '0.75rem', display: 'block' }}>
                    Forward calls to
                  </label>
                  <input
                    type="tel"
                    className="fb-input"
                    placeholder="+1 (561) 504-2459"
                    value={voiceForwardTo}
                    onChange={(e) => setVoiceForwardTo(e.target.value)}
                    autoComplete="tel"
                  />
                  <label className="fb-checkbox-row" style={{ marginTop: '0.75rem' }}>
                    <input
                      type="checkbox"
                      checked={voiceWhisperEnabled}
                      onChange={(e) => setVoiceWhisperEnabled(e.target.checked)}
                    />
                    <span style={{ fontSize: '0.85rem' }}>Play a short whisper to you before connecting</span>
                  </label>
                  <label className="fb-label" style={{ marginTop: '0.75rem', display: 'block' }}>
                    Ring timeout (seconds)
                  </label>
                  <input
                    type="number"
                    className="fb-input"
                    min={10}
                    max={60}
                    value={voiceRingTimeoutSec}
                    onChange={(e) => setVoiceRingTimeoutSec(e.target.value)}
                  />
                  <label className="fb-checkbox-row" style={{ marginTop: '0.75rem' }}>
                    <input
                      type="checkbox"
                      checked={voiceEnabled}
                      onChange={(e) => setVoiceEnabled(e.target.checked)}
                    />
                    <span style={{ fontSize: '0.85rem' }}>Enable call forwarding</span>
                  </label>
                  <label className="fb-checkbox-row" style={{ marginTop: '0.75rem' }}>
                    <input
                      type="checkbox"
                      checked={missedCallTextBackEnabled && smsNumber?.a2p?.campaignCoversMissedCall === true}
                      disabled={smsNumber?.a2p?.campaignCoversMissedCall !== true}
                      onChange={(e) => setMissedCallTextBackEnabled(e.target.checked)}
                    />
                    <span style={{ fontSize: '0.85rem' }}>
                      Send one text-back when a forwarded call is missed (default off)
                    </span>
                  </label>
                  <p className="today-muted" style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', lineHeight: 1.45 }}>
                    When on, forwarded calls ask you to press 1 to accept, so voicemail
                    doesn't count as answered.
                  </p>
                  <p className="today-muted" style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', lineHeight: 1.45 }}>
                    {MISSED_CALL_CHECKBOX_ONELINER}
                  </p>
                  <label className="fb-checkbox-row" style={{ marginTop: '0.75rem' }}>
                    <input
                      type="checkbox"
                      checked={voicemailEnabled}
                      onChange={(e) => setVoicemailEnabled(e.target.checked)}
                    />
                    <span style={{ fontSize: '0.85rem' }}>
                      Let callers leave a voicemail when you miss the call (default off)
                    </span>
                  </label>
                  {smsNumber?.a2p?.campaignCoversMissedCall !== true &&
                    smsNumber?.status === 'active' &&
                    smsNumber?.a2p?.status === 'approved' &&
                    smsNumber?.externallyManaged !== true && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <p className="today-muted" style={{ margin: 0, fontSize: '0.8rem', lineHeight: 1.45 }}>
                        Missed-call text-back needs a one-time campaign update — request it
                      </p>
                      {smsNumber?.missedCallUpgradeRequestedAt ? (
                        <p className="today-muted" style={{ margin: '0.35rem 0 0', fontSize: '0.8rem' }}>
                          Requested — we&apos;ll handle the campaign update.
                        </p>
                      ) : (
                        <button
                          type="button"
                          className="inbox-pill-btn"
                          style={{ marginTop: '0.5rem' }}
                          disabled={upgradeRequesting}
                          onClick={handleRequestMissedCallUpgrade}
                        >
                          {upgradeRequesting ? 'Requesting…' : 'Request campaign update'}
                        </button>
                      )}
                    </div>
                  )}
                  {smsNumber?.a2p?.campaignCoversMissedCall !== true &&
                    !(
                      smsNumber?.status === 'active' &&
                      smsNumber?.a2p?.status === 'approved' &&
                      smsNumber?.externallyManaged !== true
                    ) && (
                    <p className="today-muted" style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', lineHeight: 1.45 }}>
                      Your SMS campaign doesn&apos;t include missed-call text-back yet.
                    </p>
                  )}
                  <button
                    type="button"
                    className="inbox-pill-btn-primary"
                    style={{ marginTop: '0.75rem' }}
                    disabled={voiceSaving}
                    onClick={handleSaveVoiceSettings}
                  >
                    {voiceSaving ? 'Saving…' : 'Save call forwarding'}
                  </button>
                </div>
              )}
            </>
          )}
          </div>
        </div>
      )}

      {!onboardingInFlight && !onboardingComplete && (
        <>
          {!wizardStarted ? (
            <div className="today-panel settings-section-panel">
              <div className="today-panel-header">
                <span className="today-panel-title">SMS add-on</span>
              </div>
              <div className="settings-team-body">
                <p style={{ margin: '0 0 0.75rem', lineHeight: 1.6, fontSize: '0.9rem', color: 'var(--muted)' }}>
                  {SMS_TRIAL_DISCLOSURE_COPY}
                </p>
                <p style={{ margin: '0 0 1rem', lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {SMS_ADDON_PRICING_COPY}
                </p>
                <button
                  type="button"
                  className="inbox-pill-btn-primary"
                  onClick={() => {
                    setError(null);
                    setWizardStarted(true);
                  }}
                >
                  Get started
                </button>
              </div>
            </div>
          ) : (
            <>
          {renderWizardSteps()}

          {error && (
            <div className="fb-callout warn" style={{ marginBottom: '1rem' }}>
              {error}
              {error.includes('wallet') && (
                <>
                  {' '}
                  <Link to="/billing?topup=1" className="today-link-accent">Add funds</Link>
                </>
              )}
            </div>
          )}

          {step === 0 && (
            <div className="today-panel settings-section-panel">
              <div className="today-panel-header">
                <span className="today-panel-title">Business profile</span>
              </div>
              <div className="settings-team-body">
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <label className="fb-label">Legal business name *</label>
                <input
                  className="fb-input fb-input-wide"
                  value={profile.legalBusinessName}
                  onChange={(e) => setProfile({ ...profile, legalBusinessName: e.target.value })}
                />
                <label className="fb-checkbox-row" style={{ gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={profile.isSoleProprietor}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setProfile({
                        ...profile,
                        isSoleProprietor: checked,
                        ein: checked ? '' : profile.ein,
                        businessType: checked ? 'Sole Proprietorship' : 'Limited Liability Corporation',
                      });
                    }}
                  />
                  Sole proprietor (no EIN)
                </label>
                {profile.isSoleProprietor !== true && (
                  <>
                    <label className="fb-label">EIN *</label>
                    <input
                      className="fb-input fb-input-wide"
                      value={profile.ein}
                      onChange={(e) => setProfile({ ...profile, ein: e.target.value })}
                      placeholder="XX-XXXXXXX"
                    />
                    <label className="fb-label">Business type *</label>
                    <select
                      className="fb-input fb-input-wide"
                      value={profile.businessType}
                      onChange={(e) => setProfile({ ...profile, businessType: e.target.value })}
                    >
                      {BUSINESS_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <label className="fb-label">Business website *</label>
                    <input
                      className="fb-input fb-input-wide"
                      type="url"
                      value={profile.websiteUrl}
                      onChange={(e) => setProfile({ ...profile, websiteUrl: e.target.value })}
                      placeholder="https://yourbusiness.com"
                    />
                    <p className="today-muted" style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.5 }}>
                      Carriers verify your business website during registration.
                    </p>
                  </>
                )}
                <label className="fb-label">Street address *</label>
                <input className="fb-input fb-input-wide" value={profile.street} onChange={(e) => setProfile({ ...profile, street: e.target.value })} />
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 100px', gap: '0.75rem' }}>
                  <input className="fb-input" placeholder="City" value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} />
                  <input className="fb-input" placeholder="State" value={profile.state} onChange={(e) => setProfile({ ...profile, state: e.target.value })} />
                  <input className="fb-input" placeholder="ZIP" value={profile.postalCode} onChange={(e) => setProfile({ ...profile, postalCode: e.target.value })} />
                </div>
                <label className="fb-label">Contact email *</label>
                <input className="fb-input fb-input-wide" type="email" value={profile.contactEmail} onChange={(e) => setProfile({ ...profile, contactEmail: e.target.value })} />
                <label className="fb-label">{profile.isSoleProprietor === true ? 'Mobile phone *' : 'Business contact phone *'}</label>
                <input
                  className="fb-input fb-input-wide"
                  value={profile.contactPhone}
                  onChange={(e) => setProfile({ ...profile, contactPhone: e.target.value })}
                  placeholder="+1 555 123 4567"
                />
                {profile.isSoleProprietor === true && (
                  <p className="today-muted" style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.5 }}>
                    {SOLE_PROP_MOBILE_OTP_COPY}
                  </p>
                )}
              </div>
              <button type="button" className="inbox-pill-btn-primary" style={{ marginTop: '1.25rem' }} onClick={handleStep0Continue}>
                Continue
              </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="today-panel settings-section-panel">
              <div className="today-panel-header">
                <span className="today-panel-title">Pick a number</span>
              </div>
              <div className="settings-team-body">
              <div className="fb-callout info" style={{ marginBottom: '1rem', fontSize: '0.9rem', lineHeight: 1.5 }}>
                {SMS_NUMBER_PICKER_PRICING_COPY}
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <label className="fb-label" style={{ display: 'block', marginBottom: '0.35rem' }}>Area code (optional)</label>
                  <input
                    className="fb-input"
                    placeholder="e.g. 561"
                    value={areaCode}
                    maxLength={3}
                    onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ''))}
                    style={{ width: 140 }}
                  />
                </div>
                <div style={{ flex: '1 1 180px', minWidth: 160 }}>
                  <label className="fb-label" style={{ display: 'block', marginBottom: '0.35rem' }}>Contains digits/letters (optional)</label>
                  <input
                    className="fb-input fb-input-wide"
                    placeholder="e.g. 555 or FLOWERS"
                    value={numberContains}
                    onChange={(e) => setNumberContains(e.target.value.replace(/[^0-9A-Za-z*]/g, ''))}
                  />
                </div>
                <button type="button" className="inbox-pill-btn" disabled={searchingNumbers} onClick={handleSearchNumbers}>
                  {searchingNumbers ? 'Searching…' : 'Search numbers'}
                </button>
              </div>
              {numberSearchAttempted && numbers.length === 0 && (
                <div className="fb-callout warn" style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                  No numbers available for this area code. Try a nearby area code (e.g. 737, 210, 561)
                  or leave the field blank and search again for any available US number.
                </div>
              )}
              <div style={{ marginTop: '1rem', display: 'grid', gap: '0.5rem' }}>
                {numbers.map((n) => (
                  <label key={n.phoneNumber} className="fb-checkbox-row" style={{ padding: '0.75rem', border: '1px solid var(--hairline-2)', borderRadius: 8 }}>
                    <input
                      type="radio"
                      name="phone"
                      checked={selectedPhone === n.phoneNumber}
                      onChange={() => setSelectedPhone(n.phoneNumber)}
                    />
                    <span>
                      <strong>{n.phoneNumber}</strong>
                      {n.locality ? ` — ${n.locality}, ${n.region}` : ''}
                    </span>
                  </label>
                ))}
              </div>
              <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.75rem' }}>
                <button type="button" className="inbox-pill-btn" onClick={() => setStep(0)}>Back</button>
                <button
                  type="button"
                  className="inbox-pill-btn-primary"
                  disabled={!selectedPhone || previewLoading}
                  onClick={handleStep1Continue}
                >
                  {previewLoading ? 'Generating…' : 'Continue'}
                </button>
              </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="today-panel settings-section-panel">
              <div className="today-panel-header">
                <span className="today-panel-title">Review campaign</span>
              </div>
              <div className="settings-team-body">
              <label className="fb-label">Form for opt-in documentation</label>
              <select
                className="fb-input fb-input-wide"
                value={selectedFormId}
                onChange={(e) => {
                  setSelectedFormId(e.target.value);
                  setPreview(null);
                }}
              >
                <option value="">Select a form…</option>
                {forms.map((f) => (
                  <option key={f.id} value={f.id}>{f.name || f.id}</option>
                ))}
              </select>
              <button
                type="button"
                className="inbox-pill-btn"
                style={{ marginTop: '0.75rem' }}
                disabled={!selectedFormId || previewLoading}
                onClick={() => handlePreview()}
              >
                {previewLoading ? 'Generating…' : 'Generate preview'}
              </button>

              {preview && (
                <div style={{ marginTop: '1.25rem', fontSize: '0.9rem' }}>
                  <p><strong>Opt-in page:</strong>{' '}
                    <a href={preview.staticOptInUrl} target="_blank" rel="noopener noreferrer">{preview.staticOptInUrl}</a>
                  </p>
                  <p><strong>Brand type:</strong> {preview.brandType === 'SOLE_PROPRIETOR' ? 'Sole proprietor' : 'Low-volume standard'}</p>
                  <label className="fb-label">SMS confirmation message (sent once on opt-in)</label>
                  <div className="fb-callout info" style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {preview.campaign.optInMessage}
                  </div>
                  <label className="fb-label" style={{ marginTop: '1rem' }}>Message flow (vetter documentation)</label>
                  <textarea className="fb-input fb-input-wide" rows={4} readOnly value={preview.campaign.messageFlow} />
                </div>
              )}

              <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.75rem' }}>
                <button type="button" className="inbox-pill-btn" onClick={() => setStep(1)}>Back</button>
                <button type="button" className="inbox-pill-btn-primary" disabled={!preview} onClick={() => setStep(3)}>
                  Continue
                </button>
              </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="today-panel settings-section-panel">
              <div className="today-panel-header">
                <span className="today-panel-title">Confirm &amp; start</span>
              </div>
              <div className="settings-team-body">
              <ul style={{ marginTop: 0, paddingLeft: '1.25rem', lineHeight: 1.6 }}>
                <li>Number: <strong>{selectedPhone}</strong></li>
                <li>Form: <strong>{forms.find((f) => f.id === selectedFormId)?.name || selectedFormId}</strong></li>
                <li>One-time A2P setup: <strong>$49</strong> (wallet)</li>
                <li>Monthly platform fee: <strong>${smsNumber?.monthlyFeeUsd?.toFixed(2) || '19.99'}/mo</strong> (wallet)</li>
                <li>Outbound SMS: <strong>$0.015/segment</strong> from wallet</li>
              </ul>
              {walletUsd != null && (
                <p className="today-muted" style={{ marginTop: '0.5rem' }}>
                  Wallet balance: <strong>${walletUsd.toFixed(2)}</strong>{' '}
                  <Link to="/billing?topup=1" className="today-link-accent">Top up</Link>
                </p>
              )}
              <div
                style={{
                  marginTop: '1.25rem',
                  padding: '1rem',
                  border: '1px solid var(--hairline-2)',
                  borderRadius: 8,
                  background: 'var(--surface-2)',
                }}
              >
                <label className="fb-checkbox-row" style={{ alignItems: 'flex-start', gap: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={programTermsAccepted}
                    onChange={(e) => setProgramTermsAccepted(e.target.checked)}
                    style={{ marginTop: '0.2rem' }}
                  />
                  <span style={{ lineHeight: 1.55, fontSize: '0.9rem' }}>
                    I certify I will only send messages to individuals who provided consent through my
                    registered opt-in form; I will honor opt-outs immediately; I will not send prohibited
                    content (SHAFT: sex, hate, alcohol, firearms, tobacco/cannabis, or illegal content);
                    I remain responsible for my messaging under applicable law including the TCPA.{' '}
                    <a
                      href="https://gocta.ai/messaging-privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="today-link-accent"
                    >
                      SMS Messaging Privacy
                    </a>
                    {' · '}
                    <a
                      href="https://gocta.ai/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="today-link-accent"
                    >
                      Terms of Service
                    </a>
                  </span>
                </label>
              </div>
              <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.75rem' }}>
                <button type="button" className="inbox-pill-btn" onClick={() => setStep(2)}>Back</button>
                <button
                  type="button"
                  className="inbox-pill-btn-primary"
                  disabled={submitting || !programTermsAccepted}
                  onClick={handleStart}
                >
                  {submitting ? 'Starting…' : 'Start SMS onboarding'}
                </button>
              </div>
              </div>
            </div>
          )}
            </>
          )}
        </>
      )}
      </div>
    </div>
  );
}
